import { ColumnDef } from '@tanstack/react-table';
import {
    AlertCircle,
    ArrowLeft,
    Calendar,
    CheckCircle,
    Loader2,
    MapPin,
    Package,
    Pencil,
    Plus,
    Ship,
    Printer,
    Trash2,
    Truck
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import ScanHistoryCard from '../components/ScanHistoryCard';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { ProductThumbnail } from '../components/ui/aspect-ratio-image';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { createBadgeColumn, createDateColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Shipment, ShipmentType } from '../types/shipment';
import { getFirstProductImageUrl } from '../utils/productImage';
import { generateQRLabelPdf, QRLabelItem } from '../utils/qrLabelPdfGenerator';

const SCAN_BASE_URL = process.env.REACT_APP_SCAN_BASE_URL || 'https://scan.homebound.co.uk';

type Role = { key: string; name: string; description: string };
type UserType = { id: string; email: string; first_name: string; last_name: string; roles: Role[] };

const ShipmentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<UserType | null>(null);
  const [orderItems, setOrderItems] = useState<any[]>([]);
  const [selectProductForUnits, setSelectProductForUnits] = useState<any | null>(null);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [removeAssetDialogOpen, setRemoveAssetDialogOpen] = useState(false);
  const [assetToRemove, setAssetToRemove] = useState<any>(null);
  const [addItemsDialogOpen, setAddItemsDialogOpen] = useState(false);
  const [availableAssets, setAvailableAssets] = useState<any[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [addingAssets, setAddingAssets] = useState(false);
  const [assetsPage, setAssetsPage] = useState(1);
  const [assetsLimit, setAssetsLimit] = useState(10);
  const [assetsTotalPages, setAssetsTotalPages] = useState(1);
  const [assetsTotalItems, setAssetsTotalItems] = useState(0);

  const loadShipment = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/shipments/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load shipment');
      }

      const data = await response.json();
      const shipmentData = data.success ? data.data : null;
      setShipment(shipmentData);

      // Load order items if shipment has jobs
      if (shipmentData && shipmentData.jobs && shipmentData.jobs.length > 0) {
        const orderId = shipmentData.jobs[0].order_id;
        if (orderId) {
          const orderItemsResponse = await fetch(`/api/orders/${orderId}/items`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const orderItemsData = await orderItemsResponse.json();
          if (orderItemsData.success) {
            setOrderItems(orderItemsData.data || []);
          }
        }
      }
    } catch (err) {
      console.error('Error loading shipment:', err);
      setError('Failed to load shipment details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadShipment();
    // Check auth
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, [loadShipment]);

  const getShipmentTypeIcon = (type: ShipmentType) => {
    switch (type) {
      case ShipmentType.INBOUND:
        return <Ship className="h-5 w-5" />;
      case ShipmentType.OUTBOUND:
        return <Truck className="h-5 w-5" />;
      case ShipmentType.TRANSFER:
        return <Package className="h-5 w-5" />;
      default:
        return <Package className="h-5 w-5" />;
    }
  };

  const getShipmentTypeBadgeVariant = (type: ShipmentType) => {
    switch (type) {
      case ShipmentType.INBOUND:
        return 'default' as const;
      case ShipmentType.OUTBOUND:
        return 'secondary' as const;
      case ShipmentType.TRANSFER:
        return 'warning' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getShipmentStatus = (shipment: Shipment) => {
    const jobs = shipment.jobs || [];
    
    // No jobs linked - use legacy timestamp-based logic
    if (jobs.length === 0) {
      if (shipment.actual_arrival_at) {
        return { status: 'Delivered', variant: 'default' as const };
      }
      return { status: 'Pending', variant: 'secondary' as const };
    }

    // Derive status from job statuses
    const statuses = jobs.map(j => j.status);
    
    if (statuses.every(s => s === 'completed')) {
      return { status: 'Delivered', variant: 'default' as const };
    }
    if (statuses.every(s => s === 'cancelled')) {
      return { status: 'Cancelled', variant: 'secondary' as const };
    }
    if (statuses.some(s => s === 'in_progress')) {
      return { status: 'In Transit', variant: 'warning' as const };
    }
    if (statuses.some(s => s === 'assigned')) {
      return { status: 'Assigned', variant: 'secondary' as const };
    }
    return { status: 'Pending', variant: 'secondary' as const };
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'warning'> = {
      available: 'default',
      rented: 'secondary',
      maintenance: 'warning',
      retired: 'secondary',
      lost: 'destructive',
      damaged: 'destructive',
    };
    return variants[status] || 'secondary';
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  const canEdit = user?.roles?.some(role => ['admin', 'supervisor'].includes(role.key)) ?? false;

  const handlePrintQRLabels = async () => {
    if (!shipment?.asset_instances?.length) return;

    const items: QRLabelItem[] = shipment.asset_instances
      .filter((inst: any) => inst.qr_code?.code)
      .map((inst: any) => {
        const imageUrl = getFirstProductImageUrl(inst.template?.images);
        return {
          qrCodeValue: `${SCAN_BASE_URL}?q=${inst.qr_code.code}`,
          qrCodeText: inst.qr_code.code,
          productName: inst.template?.name || 'Unknown Product',
          unitNumber: inst.instance_number,
          imageUrl: imageUrl || undefined,
        };
      });

    if (items.length === 0) {
      alert('No items with assigned QR codes found in this shipment.');
      return;
    }

    await generateQRLabelPdf(items, {
      title: `Shipment: ${shipment.bl_number}`,
      filename: `qr-labels-${shipment.bl_number}.pdf`,
    });
  };

  const assetColumns: ColumnDef<any>[] = [
    {
      accessorKey: 'instance_number',
      header: 'Unit #',
      cell: ({ row }) => {
        const instance = row.original;
        const imageUrl = getFirstProductImageUrl(instance.template?.images);

        return (
          <button
            onClick={() => navigate(`/units/${instance.id}`)}
            className="flex items-center gap-3 text-left hover:text-primary"
            title="View unit details"
          >
            <ProductThumbnail
              src={imageUrl}
              alt={instance.template?.name || 'Product'}
              size="sm"
              fallback={<Package className="h-6 w-6 text-muted-foreground" />}
            />
            <div className="flex flex-col">
              <span className="font-mono font-medium underline-offset-2 hover:underline">
                {instance.instance_number}
              </span>
              {instance.template?.name && (
                <span className="text-xs text-muted-foreground">
                  {instance.template.name}
                </span>
              )}
            </div>
          </button>
        );
      },
    },
    createBadgeColumn<any>('current_status', 'Status', {
      getBadgeVariant: (value: string) => getStatusBadge(value),
      formatValue: formatStatus,
    }),
    createDateColumn<any>('created_at', 'Created'),
    {
      accessorKey: 'created_by_user',
      header: 'Created By',
      cell: ({ row }) => {
        const user = row.getValue('created_by_user') as any;
        return user ? (
          <span>{user.first_name} {user.last_name}</span>
        ) : (
          <span className="text-gray-400">—</span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        return canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleRemoveAssetClick(row.original)}
            title="Remove from shipment"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        ) : null;
      },
    },
  ];

  const handleDelete = async () => {
    if (!shipment) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/shipments/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        navigate('/shipments');
      } else {
        setErrorMessage(data.message || 'Failed to delete shipment');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error deleting shipment:', err);
      setErrorMessage('An error occurred while deleting the shipment');
      setErrorDialogOpen(true);
    }
  };

  const handleRemoveAssetClick = (asset: any) => {
    setAssetToRemove(asset);
    setRemoveAssetDialogOpen(true);
  };

  const handleRemoveAsset = async () => {
    if (!assetToRemove) return;

    // Save current scroll position
    const scrollPosition = window.scrollY;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/shipments/${id}/assets/${assetToRemove.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        setRemoveAssetDialogOpen(false);
        setAssetToRemove(null);
        await loadShipment();

        // Restore scroll position after state update
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPosition);
        });
      } else {
        setRemoveAssetDialogOpen(false);
        setErrorMessage(data.message || 'Failed to remove asset from shipment');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error removing asset:', err);
      setRemoveAssetDialogOpen(false);
      setErrorMessage('An error occurred while removing the asset');
      setErrorDialogOpen(true);
    }
  };

  const loadAvailableAssets = async (page = assetsPage, limit = assetsLimit, productId?: string) => {
    try {
      setLoadingAssets(true);
      const token = localStorage.getItem('token');
      const templateParam = productId ? `&template_id=${productId}` : '';
      const response = await fetch(`/api/asset-instances?status=available&page=${page}&limit=${limit}${templateParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load available assets');
      }

      const data = await response.json();

      console.log('[ShipmentDetails] API Response:', {
        success: data.success,
        hasPagination: !!data.pagination,
        pagination: data.pagination,
        dataLength: data.data?.length
      });

      if (data.success && data.pagination) {
        setAvailableAssets(data.data || []);
        setAssetsTotalItems(data.pagination.total);
        setAssetsTotalPages(data.pagination.totalPages || data.pagination.pages); // Support both formats

        console.log('[ShipmentDetails] Pagination State Set:', {
          page,
          limit,
          totalItems: data.pagination.total,
          totalPages: data.pagination.totalPages || data.pagination.pages
        });
      } else {
        // Fallback for non-paginated response
        setAvailableAssets(data.success ? data.data : []);
        setAssetsTotalItems((data.data || []).length);
        setAssetsTotalPages(1);
      }
    } catch (err) {
      console.error('Error loading available assets:', err);
      setErrorMessage('Failed to load available assets');
      setErrorDialogOpen(true);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleAddItems = async () => {
    if (selectedAssets.length === 0) return;

    // Save current scroll position
    const scrollPosition = window.scrollY;

    try {
      setAddingAssets(true);
      const token = localStorage.getItem('token');
      let successCount = 0;
      let errorCount = 0;
      let lastError = '';

      for (const assetId of selectedAssets) {
        try {
          const response = await fetch(`/api/shipments/${id}/assets`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ asset_instance_id: assetId }),
          });
          const data = await response.json();

          if (data.success) {
            successCount++;
          } else {
            errorCount++;
            lastError = data.message;
          }
        } catch (err) {
          errorCount++;
          lastError = 'Network error';
        }
      }

      setAddItemsDialogOpen(false);
      setSelectedAssets([]);

      if (successCount > 0) {
        await loadShipment();

        // Restore scroll position after state update
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPosition);
        });
      }

      if (errorCount > 0) {
        setErrorMessage(
          `Added ${successCount} item(s). ${errorCount} failed: ${lastError}`
        );
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error adding assets:', err);
      setErrorMessage('An error occurred while adding assets');
      setErrorDialogOpen(true);
    } finally {
      setAddingAssets(false);
    }
  };

  const handleOpenAddItems = () => {
    setAssetsPage(1); // Reset to first page
    setSelectedAssets([]); // Clear selections
    setSelectProductForUnits(null); // Reset product selection
    setAddItemsDialogOpen(true);
  };

  const handleSelectUnitsForProduct = (product: any) => {
    setSelectProductForUnits(product);
    setAssetsPage(1);
    setSelectedAssets([]);
  };

  // Reload assets when pagination changes in the add items modal
  useEffect(() => {
    if (addItemsDialogOpen && selectProductForUnits) {
      const productId = selectProductForUnits.asset_template_id;
      loadAvailableAssets(assetsPage, assetsLimit, productId);
    }
  }, [assetsPage, assetsLimit, addItemsDialogOpen, selectProductForUnits]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="shipments" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <h2 className="text-xl font-heading text-primary">Loading shipment details...</h2>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="shipments" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <h2 className="text-xl font-heading text-destructive mb-4">
                {error || 'Shipment not found'}
              </h2>
              <Button onClick={() => navigate('/shipments')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Shipments
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const { status, variant } = getShipmentStatus(shipment);

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="shipments" />

      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/shipments')} className="h-8 w-8 shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 min-w-0">
            {getShipmentTypeIcon(shipment.shipment_type)}
            <h1 className="text-2xl font-heading font-bold text-primary truncate">
              {shipment.bl_number}
            </h1>
            <Badge variant={getShipmentTypeBadgeVariant(shipment.shipment_type)}>
              {shipment.shipment_type.charAt(0).toUpperCase() + shipment.shipment_type.slice(1)}
            </Badge>
            <Badge variant={variant}>{status}</Badge>
          </div>
          <div className="flex gap-2 ml-auto shrink-0">
            {canEdit && (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate(`/shipments/${id}/edit`)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="text-2xl font-bold">
                    {shipment.asset_instances?.length || 0}
                  </p>
                </div>
                <Package className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Shipment Type</p>
                  <p className="text-lg font-bold capitalize">
                    {shipment.shipment_type}
                  </p>
                </div>
                {getShipmentTypeIcon(shipment.shipment_type)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="text-lg font-bold">{status}</p>
                </div>
                {status === 'Overdue' ? (
                  <AlertCircle className="h-8 w-8 text-destructive" />
                ) : (
                  <CheckCircle className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Created By</p>
                  <p className="text-sm font-medium">
                    {shipment.created_by_user
                      ? `${shipment.created_by_user.first_name} ${shipment.created_by_user.last_name}`
                      : 'System'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Information Card */}
        <Card>
          <CardContent className="space-y-6 pt-6">

            {/* Route Information */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Route Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Origin */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <p className="font-semibold">Origin</p>
                  </div>
                  {shipment.origin_address ? (
                    <div className="ml-5">
                      <p className="font-medium">{shipment.origin_address.address_line1}</p>
                      {shipment.origin_address.address_line2 && (
                        <p className="text-sm text-muted-foreground">{shipment.origin_address.address_line2}</p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        {shipment.origin_address.city}, {shipment.origin_address.region} {shipment.origin_address.postal_code}
                      </p>
                    </div>
                  ) : (
                    <p className="text-muted-foreground italic ml-5">No origin address</p>
                  )}
                </div>

                {/* Destination */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <p className="font-semibold">Destination</p>
                  </div>
                  {shipment.destination_address ? (
                    <div className="ml-5">
                      <p className="font-medium">{shipment.destination_address.address_line1}</p>
                      {shipment.destination_address.address_line2 && (
                        <p className="text-sm text-muted-foreground">{shipment.destination_address.address_line2}</p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        {shipment.destination_address.city}, {shipment.destination_address.region} {shipment.destination_address.postal_code}
                      </p>
                    </div>
                  ) : (
                    <p className="text-muted-foreground italic ml-5">No destination address</p>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Timeline */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Timeline
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">
                    {new Date(shipment.created_at).toLocaleDateString()}
                  </p>
                </div>
                {shipment.planned_departure_at && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Planned Departure</p>
                    <p className="font-medium">
                      {new Date(shipment.planned_departure_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {shipment.actual_departure_at && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Actual Departure</p>
                    <p className="font-medium">
                      {new Date(shipment.actual_departure_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {shipment.planned_arrival_at && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Planned Arrival</p>
                    <p className="font-medium">
                      {new Date(shipment.planned_arrival_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {shipment.actual_arrival_at && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Actual Arrival</p>
                    <p className="font-medium">
                      {new Date(shipment.actual_arrival_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Items Manifest */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Items Manifest ({shipment.asset_instances?.length || 0} items)
                </h3>
                <div className="flex gap-2">
                  {(shipment.asset_instances?.length ?? 0) > 0 && (
                    <Button variant="outline" size="sm" onClick={handlePrintQRLabels}>
                      <Printer className="h-4 w-4 mr-2" />
                      Print QR Labels
                    </Button>
                  )}
                  {canEdit && (
                    <Button onClick={handleOpenAddItems}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Items
                    </Button>
                  )}
                </div>
              </div>
              <DataTable
                columns={assetColumns}
                data={shipment.asset_instances || []}
                searchKey="instance_number"
                searchPlaceholder="Search by unit number..."
                emptyMessage="No items in this shipment"
              />
            </div>

            {id && (
              <div className="mt-6">
                <ScanHistoryCard
                  title="Scan History"
                  endpoint={`/api/scan/history/shipment/${id}`}
                  emptyMessage="No scans recorded for this shipment"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Shipment"
        description={`Are you sure you want to delete shipment ${shipment?.bl_number}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        variant="destructive"
      />

      {/* Error Message Dialog */}
      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />

      {/* Remove Asset Confirmation Dialog */}
      <ConfirmDialog
        open={removeAssetDialogOpen}
        onOpenChange={setRemoveAssetDialogOpen}
        title="Remove Asset from Shipment"
        description={`Are you sure you want to remove ${assetToRemove?.instance_number || 'this unit'} from the shipment? The unit status will be changed back to "available".`}
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={handleRemoveAsset}
        variant="destructive"
      />

      {/* Add Items Dialog */}
      <Dialog open={addItemsDialogOpen} onOpenChange={setAddItemsDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] !flex !flex-col overflow-hidden p-0 gap-0">
          <div className="px-6 pt-6 pb-4 border-b">
            <DialogHeader>
              <DialogTitle>
                {selectProductForUnits
                  ? `Select Units - ${selectProductForUnits.asset_template?.name}`
                  : 'Select Products to Add Units'}
              </DialogTitle>
              <DialogDescription>
                {selectProductForUnits
                  ? 'Select available units for this product. Only units with "available" status are shown.'
                  : 'Choose a product to select its units.'}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4" style={{ minHeight: '300px', maxHeight: 'calc(85vh - 200px)' }}>
            {!selectProductForUnits ? (
              /* Show products list */
              <div className="space-y-3 min-h-full">
                {orderItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No products in this order. Please ensure the shipment is linked to a job with an order.
                  </div>
                ) : (
                  orderItems.map((item) => {
                    const currentCount = shipment?.asset_instances?.filter(
                      (inst: any) => (inst.template?.id || inst.template_id) === item.asset_template_id
                    ).length || 0;
                    const remaining = item.quantity - currentCount;
                    const template = item.asset_template;
                    const imageUrl = getFirstProductImageUrl(template?.images);

                    return (
                      <Card key={item.id} className="overflow-hidden">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <ProductThumbnail
                              src={imageUrl}
                              alt={item.asset_template?.name || 'Product'}
                              size="md"
                              fallback={<Package className="h-12 w-12 text-muted-foreground" />}
                            />

                            <div className="flex-1">
                              <h4 className="font-semibold text-base">
                                {item.asset_template?.name || 'Product'}
                              </h4>
                              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                <span>Order Qty: {item.quantity}</span>
                                <span>•</span>
                                <span className={remaining > 0 ? 'text-orange-600 font-medium' : 'text-green-600 font-medium'}>
                                  {currentCount}/{item.quantity} added
                                </span>
                                {remaining > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="text-primary font-medium">
                                      {remaining} remaining
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>

                            <Button
                              variant={remaining > 0 ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleSelectUnitsForProduct(item)}
                              disabled={remaining === 0}
                            >
                              {remaining > 0 ? 'Select Units' : 'Complete'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            ) : (
              /* Show units for selected product */
              <div className="flex flex-col gap-4 min-h-full">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-shrink-0">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-blue-800">
                      Showing available units for this product
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectProductForUnits(null)}
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Back to Products
                    </Button>
                  </div>
                </div>

                {loadingAssets ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2">Loading available units...</span>
                  </div>
                ) : availableAssets.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No available units found for this product
                  </div>
                ) : (
                  <>
                    <div className="space-y-2 flex-1">
                      <DataTable
                        columns={[
                  {
                    id: 'select',
                    header: ({ table }) => (
                      <Checkbox
                        checked={table.getIsAllPageRowsSelected()}
                        onCheckedChange={(value) => {
                          table.toggleAllPageRowsSelected(!!value);
                          if (value) {
                            setSelectedAssets(availableAssets.map(a => a.id));
                          } else {
                            setSelectedAssets([]);
                          }
                        }}
                        aria-label="Select all"
                      />
                    ),
                    cell: ({ row }) => (
                      <Checkbox
                        checked={selectedAssets.includes(row.original.id)}
                        onCheckedChange={(value) => {
                          if (value) {
                            setSelectedAssets([...selectedAssets, row.original.id]);
                          } else {
                            setSelectedAssets(selectedAssets.filter(id => id !== row.original.id));
                          }
                        }}
                        aria-label="Select row"
                      />
                    ),
                  },
                  {
                    accessorKey: 'instance_number',
                    header: 'Unit #',
                    cell: ({ row }) => {
                      const instance = row.original;
                      const imageUrl = getFirstProductImageUrl(instance.template?.images);

                      return (
                        <div className="flex items-center gap-3">
                          <ProductThumbnail
                            src={imageUrl}
                            alt={instance.template?.name || 'Product'}
                            size="sm"
                            fallback={<Package className="h-5 w-5 text-muted-foreground" />}
                          />
                          <div className="flex flex-col">
                            <span className="font-mono font-medium">
                              {instance.instance_number}
                            </span>
                            {instance.template?.name && (
                              <span className="text-xs text-muted-foreground">
                                {instance.template.name}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    },
                  },
                ]}
                data={availableAssets}
                searchKey="instance_number"
                searchPlaceholder="Search by unit number..."
                emptyMessage="No available units found"
                serverSide={true}
                pagination={{
                  total: assetsTotalItems,
                  page: assetsPage,
                  limit: assetsLimit,
                  pages: assetsTotalPages,
                }}
                onPaginationChange={(newPage, newLimit) => {
                  if (newLimit !== assetsLimit) {
                    setAssetsLimit(newLimit);
                    setAssetsPage(1);
                  } else {
                    setAssetsPage(newPage);
                  }
                        }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-background">
            <div className="flex items-center justify-between w-full">
              <div className="text-sm text-muted-foreground">
                {selectProductForUnits && `${selectedAssets.length} unit(s) selected`}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  className="border border-input"
                  onClick={() => {
                    setSelectedAssets([]);
                    setSelectProductForUnits(null);
                    setAddItemsDialogOpen(false);
                  }}
                >
                  Cancel
                </Button>
                {selectProductForUnits && (
                  <Button
                    onClick={handleAddItems}
                    disabled={selectedAssets.length === 0 || addingAssets}
                  >
                    {addingAssets ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      `Add ${selectedAssets.length} Item${selectedAssets.length !== 1 ? 's' : ''}`
                    )}
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShipmentDetails;
